/**
 * ToolError 组件
 * 用于显示工具执行错误，基于错误类型映射 i18n 错误字符串
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep } from '../../../../../types';
import { FiAlertTriangle, FiXCircle, FiRefreshCw } from 'react-icons/fi';

interface ToolErrorProps {
  step: ProcessStep;
}

export const ToolError: React.FC<ToolErrorProps> = ({ step }) => {
  const { t } = useTranslation(['v3/chat']);

  if (step.type !== 'tool_error') {
    return null;
  }

  // 获取错误状态
  const errorStatus = step.status || 'system_error';

  // 判断是否为工具本身的错误（有工具名）还是执行过程中的其他错误（无工具名）
  const isToolSpecificError = !!step.toolName;

  // 获取本地化的错误消息
  const getErrorMessage = (status: string): string => {
    // 根据是否有工具名选择不同的翻译键前缀
    const keyPrefix = isToolSpecificError ? 'toolErrors' : 'processErrors';
    const key = `v3/chat:${keyPrefix}.${status}`;
    const translated = t(key);

    // 如果翻译键不存在，返回默认消息
    if (translated === key) {
      const fallbackKey = `v3/chat:${keyPrefix}.system_error`;
      const fallbackTranslated = t(fallbackKey);
      return fallbackTranslated === fallbackKey ? t('v3/chat:common.unknownError') : fallbackTranslated;
    }

    return translated;
  };

  // 获取本地化的错误标题
  const getErrorTitle = (): string => {
    if (isToolSpecificError) {
      return t('v3/chat:toolErrors.title');
    } else {
      return t('v3/chat:processErrors.title');
    }
  };

  // 根据错误类型获取图标
  const getErrorIcon = (status: string) => {
    switch (status) {
      case 'validation_error':
        return <FiAlertTriangle className="w-4 h-4" />;
      case 'system_error':
      case 'update_error':
      case 'fetch_error':
        return <FiXCircle className="w-4 h-4" />;
      case 'fetch_project_error':
        return <FiRefreshCw className="w-4 h-4" />;
      default:
        return <FiXCircle className="w-4 h-4" />;
    }
  };

  // 根据错误类型获取颜色样式
  const getErrorStyles = (status: string) => {
    switch (status) {
      case 'validation_error':
        return {
          container: 'border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-900/20',
          icon: 'text-orange-600 dark:text-orange-400',
          title: 'text-orange-800 dark:text-orange-200',
          message: 'text-orange-700 dark:text-orange-300'
        };
      case 'fetch_project_error':
        return {
          container: 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
          icon: 'text-yellow-600 dark:text-yellow-400',
          title: 'text-yellow-800 dark:text-yellow-200',
          message: 'text-yellow-700 dark:text-yellow-300'
        };
      default:
        return {
          container: 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20',
          icon: 'text-red-600 dark:text-red-400',
          title: 'text-red-800 dark:text-red-200',
          message: 'text-red-700 dark:text-red-300'
        };
    }
  };

  const styles = getErrorStyles(errorStatus);
  const errorMessage = getErrorMessage(errorStatus);
  const errorTitle = getErrorTitle();

  return (
    <div className={`border rounded-lg p-3 ${styles.container}`}>
      {/* 错误标题 */}
      <div className="flex items-center gap-2 mb-2">
        <div className={styles.icon}>
          {getErrorIcon(errorStatus)}
        </div>
        <h4 className={`text-sm font-medium ${styles.title}`}>
          {errorTitle}
        </h4>
      </div>

      {/* 错误消息 */}
      <div className={`text-sm ${styles.message}`}>
        {errorMessage}
      </div>

      {/* 时间戳（不显示工具名称） */}
      <div className="mt-2 flex items-center justify-end text-xs text-gray-500 dark:text-gray-400">
        <span>{new Date(step.timestamp).toLocaleString()}</span>
      </div>

      {/* 详细错误信息（如果有） */}
      {step.result && typeof step.result === 'object' && step.result.details && (
        <details className="mt-2">
          <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
            {t('v3/chat:common.viewDetails')}
          </summary>
          <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">
            {step.result.details}
          </div>
        </details>
      )}
    </div>
  );
};

export default ToolError;
